import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const useActivityParticipation = () => {
  const navigate = useNavigate();
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchParticipations = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.eventID) queryObj.eventID = params.eventID;
        if (params.calendarID) queryObj.calendarID = params.calendarID;
        if (params.termID) queryObj.termID = params.termID;
        if (params.acadyrID) queryObj.acadyrID = params.acadyrID;
        if (params.studentID) queryObj.studentID = params.studentID;
        if (params.involvementLevel)
          queryObj.involvementLevel = params.involvementLevel;
        if (params.performanceRating)
          queryObj.performanceRating = params.performanceRating;
        if (params.hasAward !== undefined && params.hasAward !== null)
          queryObj.hasAward = params.hasAward;
        if (params.category) queryObj.category = params.category;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.activityParticipation}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setParticipations(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(
            response,
            "Failed to fetch participation records",
          );
        }
      } catch (error) {
        message.error("Error fetching participation records");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const recordParticipation = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.activityParticipation,
          data,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Participation saved",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to save participation");
        return null;
      } catch (error) {
        message.error("Error saving participation");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updateParticipation = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.activityParticipationDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Participation updated",
            "text-success",
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to update participation");
        return null;
      } catch (error) {
        message.error("Error updating participation");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deleteParticipation = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.activityParticipationDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          Utilities.showNotification(
            undefined,
            "Record deleted",
            "text-success",
          );
          return true;
        }
        Utilities.showError(response, "Failed to delete record");
        return false;
      } catch (error) {
        message.error("Error deleting record");
        console.error(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchEventSummary = useCallback(
    async (eventID) => {
      try {
        const url = `${domainSettings.urls.academics.activityParticipationEventSummary}?eventID=${eventID}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data;
        return null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  return {
    participations,
    loading,
    fetchParticipations,
    recordParticipation,
    updateParticipation,
    deleteParticipation,
    fetchEventSummary,
  };
};

export default useActivityParticipation;
